// File: edgelexapp/webelieve-frontend/src/features/dms/stores/useDocumentCheckoutStore.ts
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useDocumentAPI } from '../DocumentAPI';
import { CheckoutStatus } from '../components/CheckoutStatusTag';

export interface CheckoutOptions {
  createNewVersion: boolean;
  versionComment?: string;
  forceCheckin?: boolean;
  forceCheckinReason?: string;
  workingCopyData?: {
    content?: string;
    filePath?: string;
    metadata?: {
      sessionId?: string;
      deviceInfo?: string;
      autosaveCount?: number;
    };
  };
}

export interface WorkingCopyData {
  content?: string;
  filePath?: string;
  metadata?: {
    sessionId?: string;
    deviceInfo?: string;
    autosaveCount?: number;
    unsavedChanges?: boolean;
  };
}

export interface DocumentCheckoutState {
  // State
  checkoutStatuses: Record<string, CheckoutStatus>;
  workingCopies: Record<string, WorkingCopyData>;
  isEditing: Record<string, boolean>;
  hasUnsavedChanges: Record<string, boolean>;
  loadingStates: Record<string, boolean>;
  errors: Record<string, string | null>;
  offlineMode: Record<string, boolean>;
  
  // Actions
  setCheckoutStatus: (documentId: string, status: CheckoutStatus) => void;
  setWorkingCopy: (documentId: string, data: WorkingCopyData) => void;
  setEditing: (documentId: string, isEditing: boolean) => void;
  setUnsavedChanges: (documentId: string, hasChanges: boolean) => void;
  setLoading: (documentId: string, isLoading: boolean) => void;
  setError: (documentId: string, error: string | null) => void;
  setOfflineMode: (documentId: string, isOffline: boolean) => void;
  
  // API Actions
  checkoutDocument: (documentId: string, userId: string) => Promise<void>;
  checkinDocument: (documentId: string, options: CheckoutOptions) => Promise<void>;
  saveWorkingCopy: (documentId: string, content: string, metadata?: Record<string, unknown>) => Promise<void>;
  getCheckoutStatus: (documentId: string) => Promise<void>;
  forceCheckinDocument: (documentId: string, reason: string) => Promise<void>;
  
  // Utility Actions
  clearDocumentState: (documentId: string) => void;
  clearAllStates: () => void;
  refreshAllStatuses: (documentIds: string[]) => Promise<void>;
}

export const useDocumentCheckoutStore = create<DocumentCheckoutState>()(
  devtools(
    (set, get) => ({
      // Initial state
      checkoutStatuses: {},
      workingCopies: {},
      isEditing: {},
      hasUnsavedChanges: {},
      loadingStates: {},
      errors: {},
      offlineMode: {},

      // State setters
      setCheckoutStatus: (documentId: string, status: CheckoutStatus) =>
        set((state) => ({
          checkoutStatuses: {
            ...state.checkoutStatuses,
            [documentId]: status
          }
        })),

      setWorkingCopy: (documentId: string, data: WorkingCopyData) =>
        set((state) => ({
          workingCopies: {
            ...state.workingCopies,
            [documentId]: data
          }
        })),

      setEditing: (documentId: string, isEditing: boolean) =>
        set((state) => ({
          isEditing: {
            ...state.isEditing,
            [documentId]: isEditing
          }
        })),

      setUnsavedChanges: (documentId: string, hasChanges: boolean) =>
        set((state) => ({
          hasUnsavedChanges: {
            ...state.hasUnsavedChanges,
            [documentId]: hasChanges
          }
        })),

      setLoading: (documentId: string, isLoading: boolean) =>
        set((state) => ({
          loadingStates: {
            ...state.loadingStates,
            [documentId]: isLoading
          }
        })),

      setError: (documentId: string, error: string | null) =>
        set((state) => ({
          errors: {
            ...state.errors,
            [documentId]: error
          }
        })),

      setOfflineMode: (documentId: string, isOffline: boolean) =>
        set((state) => ({
          offlineMode: {
            ...state.offlineMode,
            [documentId]: isOffline
          }
        })),

      // API Actions
      checkoutDocument: async (documentId: string, userId: string) => {
        const { setLoading, setError, setCheckoutStatus, setEditing } = get();
        
        setLoading(documentId, true);
        setError(documentId, null);

        try {
          // This would need to be called from a component with access to the API
          // For now, we'll just update the state
          setCheckoutStatus(documentId, {
            isCheckedOut: true,
            checkedOutBy: {
              user_id: userId,
              keycloak_user_sub: userId,
              role: 'user'
            },
            checkedOutAt: new Date().toISOString(),
            checkedOutExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            hasWorkingCopy: false,
            lastLocalSaveAt: null,
            workingCopyMetadata: null
          });
          setEditing(documentId, true);
        } catch (error) {
          setError(documentId, error instanceof Error ? error.message : 'Failed to checkout document');
        } finally {
          setLoading(documentId, false);
        }
      },

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      checkinDocument: async (documentId: string, options: CheckoutOptions) => {
        const { setLoading, setError, setCheckoutStatus, setEditing, setWorkingCopy } = get();
        
        setLoading(documentId, true);
        setError(documentId, null);

        try {
          // This would need to be called from a component with access to the API
          // For now, we'll just update the state
          setCheckoutStatus(documentId, {
            isCheckedOut: false,
            checkedOutBy: null,
            checkedOutAt: null,
            checkedOutExpiresAt: null,
            hasWorkingCopy: false,
            lastLocalSaveAt: null,
            workingCopyMetadata: null
          });
          setEditing(documentId, false);
          setWorkingCopy(documentId, {});
        } catch (error) {
          setError(documentId, error instanceof Error ? error.message : 'Failed to check-in document');
        } finally {
          setLoading(documentId, false);
        }
      },

      saveWorkingCopy: async (documentId: string, content: string, metadata?: Record<string, unknown>) => {
        const { setWorkingCopy, setUnsavedChanges } = get();
        
        const workingCopyData: WorkingCopyData = {
          content,
          metadata: {
            sessionId: (metadata?.sessionId as string) || `session_${Date.now()}`,
            deviceInfo: (metadata?.deviceInfo as string) || navigator.userAgent,
            autosaveCount: ((metadata?.autosaveCount as number) || 0) + 1,
            unsavedChanges: true
          }
        };

        setWorkingCopy(documentId, workingCopyData);
        setUnsavedChanges(documentId, true);
      },

      getCheckoutStatus: async (documentId: string) => {
        const { setLoading, setError, setCheckoutStatus } = get();
        
        setLoading(documentId, true);
        setError(documentId, null);

        try {
          // This would need to be called from a component with access to the API
          // For now, we'll just simulate a successful response
          const mockStatus: CheckoutStatus = {
            isCheckedOut: false,
            checkedOutBy: null,
            checkedOutAt: null,
            checkedOutExpiresAt: null,
            hasWorkingCopy: false,
            lastLocalSaveAt: null,
            workingCopyMetadata: null
          };
          
          setCheckoutStatus(documentId, mockStatus);
        } catch (error) {
          setError(documentId, error instanceof Error ? error.message : 'Failed to fetch checkout status');
        } finally {
          setLoading(documentId, false);
        }
      },

      forceCheckinDocument: async (documentId: string) => {
        const { setLoading, setError, setCheckoutStatus, setEditing, setWorkingCopy } = get();
        
        setLoading(documentId, true);
        setError(documentId, null);

        try {
          // This would need to be called from a component with access to the API
          // For now, we'll just update the state
          setCheckoutStatus(documentId, {
            isCheckedOut: false,
            checkedOutBy: null,
            checkedOutAt: null,
            checkedOutExpiresAt: null,
            hasWorkingCopy: false,
            lastLocalSaveAt: null,
            workingCopyMetadata: null
          });
          setEditing(documentId, false);
          setWorkingCopy(documentId, {});
        } catch (error) {
          setError(documentId, error instanceof Error ? error.message : 'Failed to force check-in document');
        } finally {
          setLoading(documentId, false);
        }
      },

      // Utility Actions
      clearDocumentState: (documentId: string) =>
        set((state) => {
          const newState = { ...state };
          delete newState.checkoutStatuses[documentId];
          delete newState.workingCopies[documentId];
          delete newState.isEditing[documentId];
          delete newState.hasUnsavedChanges[documentId];
          delete newState.loadingStates[documentId];
          delete newState.errors[documentId];
          return newState;
        }),

      clearAllStates: () =>
        set({
          checkoutStatuses: {},
          workingCopies: {},
          isEditing: {},
          hasUnsavedChanges: {},
          loadingStates: {},
          errors: {}
        }),

      refreshAllStatuses: async (documentIds: string[]) => {
        const { getCheckoutStatus } = get();
        
        await Promise.all(
          documentIds.map(documentId => getCheckoutStatus(documentId))
        );
      }
    }),
    {
      name: 'document-checkout-store',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

// Hook for using the store with API integration
export const useDocumentCheckoutWithAPI = () => {
  const documentAPI = useDocumentAPI();
  const store = useDocumentCheckoutStore();

  return {
    ...store,
    
    // Enhanced API actions that actually call the backend
    checkoutDocument: async (documentId: string, userId: string) => {
      const { setLoading, setError, setCheckoutStatus, setEditing } = store;
      
      setLoading(documentId, true);
      setError(documentId, null);

      try {
        const result = await documentAPI.checkoutDocument(documentId, {
          timeoutHours: 24,
          sessionId: `session_${Date.now()}`,
          deviceInfo: navigator.userAgent
        });

        if (result.success) {
          // Fetch the actual checkout status from backend to get complete user info
          const statusResponse = await documentAPI.getCheckoutStatus(documentId);
          if (statusResponse.success) {
            setCheckoutStatus(documentId, statusResponse.checkoutStatus);
          } else {
            // Fallback to mock status if fetch fails
            const status: CheckoutStatus = {
              isCheckedOut: true,
              checkedOutBy: {
                user_id: userId,
                keycloak_user_sub: userId,
                role: 'user',
                name: 'Current User' // Fallback name
              },
              checkedOutAt: new Date().toISOString(),
              checkedOutExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
              hasWorkingCopy: false,
              lastLocalSaveAt: null,
              workingCopyMetadata: null
            };
            setCheckoutStatus(documentId, status);
          }
          setEditing(documentId, true);
        } else {
          setError(documentId, result.message || 'Failed to checkout document');
        }
      } catch (error) {
        setError(documentId, error instanceof Error ? error.message : 'Failed to checkout document');
      } finally {
        setLoading(documentId, false);
      }
    },

    checkinDocument: async (documentId: string, options: CheckoutOptions) => {
      const { setLoading, setError, setCheckoutStatus, setEditing, setWorkingCopy } = store;
      
      setLoading(documentId, true);
      setError(documentId, null);

      try {
        const result = await documentAPI.checkinDocument(documentId, options);
        
        if (result.success) {
          const status: CheckoutStatus = {
            isCheckedOut: false,
            checkedOutBy: null,
            checkedOutAt: null,
            checkedOutExpiresAt: null,
            hasWorkingCopy: false,
            lastLocalSaveAt: null,
            workingCopyMetadata: null
          };
          
          setCheckoutStatus(documentId, status);
          setEditing(documentId, false);
          setWorkingCopy(documentId, {});
        } else {
          setError(documentId, result.message || 'Failed to check-in document');
        }
      } catch (error) {
        setError(documentId, error instanceof Error ? error.message : 'Failed to check-in document');
      } finally {
        setLoading(documentId, false);
      }
    },

    saveWorkingCopy: async (documentId: string, content: string, metadata?: Record<string, unknown>) => {
      const { setWorkingCopy, setUnsavedChanges, checkoutStatuses, offlineMode } = store;
      
      // CRITICAL: Only allow working copy saves if BOTH conditions are met:
      // 1. Document is checked out
      // 2. User explicitly clicked "Offline" button
      const checkoutStatus = checkoutStatuses[documentId];
      const isCheckedOut = checkoutStatus?.isCheckedOut || false;
      const isOfflineModeEnabled = offlineMode[documentId] || false;
      
      if (!isCheckedOut) {
        console.warn('[DocumentCheckoutStore] Cannot save working copy: Document is not checked out');
        throw new Error('Document must be checked out to save working copy');
      }
      
      if (!isOfflineModeEnabled) {
        console.warn('[DocumentCheckoutStore] Cannot save working copy: Offline mode not enabled. User must click "Offline" button first.');
        throw new Error('Offline mode must be enabled to save working copy. Click the "Offline" button first.');
      }
      
      console.log('[DocumentCheckoutStore] ✅ Saving working copy - Document is checked out AND offline mode enabled');
      
      try {
        const result = await documentAPI.saveWorkingCopy(documentId, {
          content,
          metadata: {
            sessionId: (metadata?.sessionId as string) || `session_${Date.now()}`,
            deviceInfo: (metadata?.deviceInfo as string) || navigator.userAgent,
            autosaveCount: ((metadata?.autosaveCount as number) || 0) + 1
          }
        });

        if (result.success) {
          const workingCopyData: WorkingCopyData = {
            content,
            metadata: {
              sessionId: (metadata?.sessionId as string) || `session_${Date.now()}`,
              deviceInfo: (metadata?.deviceInfo as string) || navigator.userAgent,
              autosaveCount: ((metadata?.autosaveCount as number) || 0) + 1,
              unsavedChanges: false
            }
          };

          setWorkingCopy(documentId, workingCopyData);
          setUnsavedChanges(documentId, false);
        }
      } catch (error) {
        console.error('Failed to save working copy:', error);
        // Don't set error state for working copy saves as they're background operations
      }
    },

    getCheckoutStatus: async (documentId: string) => {
      const { setLoading, setError, setCheckoutStatus } = store;
      
      setLoading(documentId, true);
      setError(documentId, null);

      try {
        const response = await documentAPI.getCheckoutStatus(documentId);
        
        if (response.success) {
          setCheckoutStatus(documentId, response.checkoutStatus);
        } else {
          setError(documentId, 'Failed to fetch checkout status');
        }
      } catch (error) {
        // Don't set error state for 401/404 errors to prevent infinite loops
        if (error instanceof Error) {
          if (error.message.includes('401') || error.message.includes('unauthorized')) {
            console.warn('Authentication error for checkout status:', error.message);
            // Don't set error state for auth errors
            return;
          } else if (error.message.includes('404') || error.message.includes('not found')) {
            console.warn('Document not found for checkout status:', error.message);
            // Don't set error state for 404 errors
            return;
          }
        }
        
        setError(documentId, error instanceof Error ? error.message : 'Failed to fetch checkout status');
      } finally {
        setLoading(documentId, false);
      }
    },

    forceCheckinDocument: async (documentId: string, reason: string) => {
      const { setLoading, setError, setCheckoutStatus, setEditing, setWorkingCopy } = store;
      
      setLoading(documentId, true);
      setError(documentId, null);

      try {
        const result = await documentAPI.forceCheckinDocument(documentId, reason);
        
        if (result.success) {
          const status: CheckoutStatus = {
            isCheckedOut: false,
            checkedOutBy: null,
            checkedOutAt: null,
            checkedOutExpiresAt: null,
            hasWorkingCopy: false,
            lastLocalSaveAt: null,
            workingCopyMetadata: null
          };
          
          setCheckoutStatus(documentId, status);
          setEditing(documentId, false);
          setWorkingCopy(documentId, {});
        } else {
          setError(documentId, result.message || 'Failed to force check-in document');
        }
      } catch (error) {
        setError(documentId, error instanceof Error ? error.message : 'Failed to force check-in document');
      } finally {
        setLoading(documentId, false);
      }
    }
  };
}; 